import React from 'react';
import { motion } from 'framer-motion';
import { Palette, Users, Lightbulb, Handshake } from 'lucide-react';

const designHighlights = [
  {
    icon: Palette,
    title: 'Design-Led Approach',
    description: 'Design is at the heart of everything we do. We believe in creating products that stand out through innovative design and attention to detail.',
  },
  {
    icon: Users,
    title: 'In-House Design Team',
    description: 'Our dedicated design team works continuously on new concepts, trends, and product development to stay ahead of market demands.',
  },
  {
    icon: Handshake,
    title: 'Collaborative Partnership',
    description: 'We work in tandem with your buying departments, providing tailormade input that perfectly aligns with your brand vision and requirements.',
  },
  {
    icon: Lightbulb,
    title: 'Constant Innovation',
    description: 'From initial concept to final product, our designers are constantly involved, ensuring every piece reflects creativity and market relevance.',
  },
];

const DesignSection = () => {
  return (
    <section 
      id="design"
      data-testid="design-section"
      className="py-24 md:py-32 bg-slate-900 text-white relative overflow-hidden"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-96 h-96 bg-rose-500 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-amber-500 rounded-full blur-3xl" />
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="font-sans text-sm font-medium tracking-[0.3em] uppercase text-rose-400 mb-4">
              Design Excellence
            </p>
            <h2 className="font-serif text-4xl md:text-5xl font-normal tracking-tight text-white mb-6">
              Adding Value<br />
              <span className="text-rose-400">Through Design</span>
            </h2>
            <div className="space-y-6 font-sans text-lg leading-relaxed text-white/70">
              <p>
                We are a <strong className="text-white">design-led company</strong> that believes in 
                the power of creative innovation. Our in-house design team provides constant input 
                to our customers, working closely from concept to completion.
              </p>
              <p>
                Our designers are not just support staff — they are integral partners in your 
                product development journey. We work <strong className="text-white">in tandem with 
                buying departments</strong> to understand your brand DNA, market positioning, and 
                customer preferences.
              </p>
              <p>
                The result? <strong className="text-white">Tailormade design input</strong> that 
                perfectly suits your needs, helping you stay ahead of trends while maintaining 
                your unique brand identity.
              </p>
            </div>

            {/* Stats */}
            <div className="mt-10 pt-10 border-t border-white/10 grid grid-cols-2 gap-6">
              <div>
                <p className="font-serif text-3xl md:text-4xl font-medium text-rose-400">300+</p>
                <p className="font-sans text-sm text-white/50 uppercase tracking-wider mt-1">Designs/Year</p>
              </div>
              <div>
                <p className="font-serif text-3xl md:text-4xl font-medium text-rose-400">100%</p>
                <p className="font-sans text-sm text-white/50 uppercase tracking-wider mt-1">In-House</p>
              </div>
            </div>
          </motion.div>

          {/* Right Content - Image Gallery + Highlights */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
            className="space-y-6"
          >
            {/* Image Gallery */}
            <div className="grid grid-cols-2 gap-4">
              <div className="aspect-[4/5] overflow-hidden rounded-lg">
                <img
                  src="https://customer-assets.emergentagent.com/job_fashion-exporter/artifacts/f6ncz532_Pv-YZV5q1H926-JMw2_F9.avif"
                  alt="Designer Dress Sample"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="aspect-[4/5] overflow-hidden rounded-lg">
                <img
                  src="https://customer-assets.emergentagent.com/job_fashion-exporter/artifacts/n009u7hr_UINfT7yE6rJMxSOHkXj3S.avif"
                  alt="Product Showroom"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                />
              </div>
            </div>
            
            {/* Highlights Grid */}
            <div className="grid grid-cols-2 gap-4">
              {designHighlights.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                  className="group p-4 bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 hover:border-rose-400/30 transition-all duration-500"
                >
                  <item.icon 
                    size={24} 
                    className="text-rose-400 mb-2" 
                    strokeWidth={1.5} 
                  />
                  <h3 className="font-serif text-base text-white mb-1">
                    {item.title}
                  </h3>
                  <p className="font-sans text-xs text-white/60 leading-relaxed">
                    {item.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default DesignSection;
